import React from 'react';
/**
 * QuestionCardClean — Card wrapper for question display.
 *
 * Renders the question text with LaTeX support, optional stimulus
 * visual aids, and the answer area (children). Uses the question type
 * registry to determine which types have built-in visuals.
 *
 * Co-located in practice/ domain (CL-1 s63).
 */
import { motion } from 'framer-motion';
import { type Question, type QuestionType } from '../../content/types';
import { QUESTION_TYPE_REGISTRY } from '../../content/questions/questionTypeRegistry';
import { VisualAidFactory } from '../../visuals';
import { LatexRenderer } from '../../components/ui/LatexRenderer';
// Derive from registry — no fragile hardcoded list to maintain
const interactiveTypesWithBuiltInVisuals = new Set(Object.entries(QUESTION_TYPE_REGISTRY).filter(([_, config]) => config.hasBuiltInVisuals).map(([type]) => type));
interface QuestionCardCleanProps {
  question: Question;
  children: React.ReactNode;
}
export function QuestionCardClean({
  question,
  children
}: QuestionCardCleanProps) {
  const shouldShowStimulusAids = !interactiveTypesWithBuiltInVisuals.has(question.questionType || '');
  const stimulusVisuals = shouldShowStimulusAids ? question.stimulusAids ?? [] : [];
  return <motion.div initial={{
    opacity: 0,
    scale: 0.98
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.3
  }} className="w-full max-w-3xl mx-auto" data-id="element-6895">
      <div className="bg-white rounded-2xl md:rounded-3xl border-2 border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden" data-id="element-6896">
        {/* Question Text */}
        <div className="p-5 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-b from-white to-slate-50/30" data-id="element-6897">
          <div className="text-fluid-xl md:text-fluid-2xl font-bold text-slate-900 leading-relaxed whitespace-pre-line" data-id="element-6898">
            <LatexRenderer content={question.question} data-id="element-6899" />
          </div>
        </div>

        {/* Stimulus Aids Zone — compact visual only */}
        {stimulusVisuals.length > 0 && <div className="p-4 sm:p-6 md:p-8 bg-slate-50/50 border-b border-slate-100 space-y-4 md:space-y-6" data-id="element-6900">
            {stimulusVisuals.map((aid, index) => <VisualAidFactory key={index} visualAid={aid} isCorrect={undefined} mode="stimulus" data-id="element-6901" />)}
          </div>}

        {/* Answer Area */}
        <div className="p-5 sm:p-6 md:p-8" data-id="element-6902">{children}</div>
      </div>
    </motion.div>;
}